import React, { useState, useRef } from 'react';
import '../index.css'

function Process() {
    const processRef = useRef(null);
    
    // Helper function to get the correct color classes
    const getColorClasses = (color, type) => {
        const colorMap = {
            indigo: {
                bg: "bg-indigo-500",
                bgLight: "bg-indigo-500/10",
                text: "text-indigo-400",
                border: "border-indigo-500/30",
                borderActive: "border-indigo-500/70",
                shadow: "shadow-indigo-500/20"
            },
            blue: {
                bg: "bg-blue-500",
                bgLight: "bg-blue-500/10",
                text: "text-blue-400",
                border: "border-blue-500/30",
                borderActive: "border-blue-500/70",
                shadow: "shadow-blue-500/20"
            },
            purple: {
                bg: "bg-purple-500",
                bgLight: "bg-purple-500/10",
                text: "text-purple-400",
                border: "border-purple-500/30",
                borderActive: "border-purple-500/70",
                shadow: "shadow-purple-500/20"
            },
            cyan: {
                bg: "bg-cyan-500",
                bgLight: "bg-cyan-500/10",
                text: "text-cyan-400",
                border: "border-cyan-500/30",
                borderActive: "border-cyan-500/70",
                shadow: "shadow-cyan-500/20"
            }
        };
        
        return colorMap[color][type];
    };
    
    // Define the process steps content
    const steps = [
        {
            number: "01",
            title: "Discovery",
            description: "We begin by understanding your current workflows, identifying pain points, and analyzing where technology can create the most impact.",
            tags: ["Needs Analysis", "Process Mapping"],
            color: "indigo"
        },
        {
            number: "02",
            title: "Design",
            description: "Our team creates detailed specifications and prototypes for your custom solution with intuitive user experience and scalable architecture.",
            tags: ["UI/UX Design", "Architecture Planning"],
            color: "blue"
        },
        {
            number: "03",
            title: "Implementation",
            description: "We build your solution using modern technologies and industry best practices with an agile development process for regular feedback.",
            tags: ["Development", "Testing", "Integration"],
            color: "purple"
        },
        {
            number: "04",
            title: "Optimization",
            description: "After deployment, we continuously monitor performance, gather user feedback, and implement improvements to evolve with your business.",
            tags: ["Performance Tuning", "Ongoing Support"],
            color: "cyan"
        }
    ];

    // Define the path coordinates for the airplane
    // Format: [x, y] where x is horizontal offset (px) from center, y is vertical position (px)
    const getPathPoints = (height) => {
        return [
            [0, 0],                  // Start point (top)
            [0, height * 0.25],      // 25% down
            [0, height * 0.5],       // 50% down
            [0, height * 0.75],      // 75% down
            [0, height]              // Bottom of the line (100%)
        ];
    };

    return (
        <section 
            id="process" 
            className="relative py-16 bg-gradient-to-b from-[#0a0a16] to-[#131724]"
            style={{ 
                overflow: "hidden"
            }}
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
                {/* Section header */}
                <div className="text-center">
                    <div className="inline-block bg-indigo-900/30 text-indigo-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
                        Our Methodology
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">How We Transform Your Workflow</h2>
                    <div className="w-16 h-1 bg-indigo-500 mx-auto mb-4"></div>
                    <p className="text-gray-300 max-w-2xl mx-auto mb-12">
                        We take a systematic approach to streamlining your business processes, 
                        optimizing each step from discovery to implementation.
                    </p>
                </div>

                {/* Process timeline */}
                <div 
                    ref={processRef} 
                    className="relative mb-28"
                    style={{ height: "auto", minHeight: "600px", marginTop: "0" }}
                >
                    {/* Center timeline - hidden on mobile */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-indigo-500/80 via-blue-500/50 to-indigo-500/80 rounded-full hidden md:block"></div>

                    {/* Process steps */}
                    <div className="md:relative">
                        {steps.map((step, index) => {
                            const isEven = index % 2 === 0;
                            const stepTop = `${60 + index * 120}px`;
                            
                            return (
                                <div 
                                    key={index}
                                    className={`mb-8 md:mb-0 md:absolute ${isEven ? 'md:left-1/2 md:pr-8 md:-translate-x-full' : 'md:left-1/2 md:pl-8'}`}
                                    style={{ 
                                        top: stepTop,
                                        maxWidth: '100%',
                                        '@media (minWidth: 768px)': {
                                            maxWidth: '42%'
                                        }
                                    }}
                                >
                                    {/* Step content */}
                                    <div 
                                        className={`bg-[#1d1c28] ${getColorClasses(step.color, 'border')} p-6 rounded-xl shadow-lg 
                                            transform transition-all duration-500 hover:translate-y-[-5px] ${
                                            getColorClasses("indigo", 'borderActive') + ' ' + getColorClasses("indigo", 'shadow')
                                        }`}
                                    >
                                        <div className={`flex items-center ${isEven ? 'md:justify-end' : ''} mb-4`}>
                                            <span className={`inline-block ${getColorClasses(step.color, 'bgLight')} ${getColorClasses(step.color, 'text')} font-bold rounded-lg px-3 py-1 mr-2`}>
                                                {step.number}
                                            </span>
                                            <h3 className="text-xl font-bold text-white">
                                                {step.title}
                                            </h3>
                                        </div>
                                        <p className="text-gray-300">
                                            {step.description}
                                        </p>
                                        <div className={`flex ${isEven ? 'md:justify-end' : ''} mt-4`}>
                                            <div className={`flex items-center ${getColorClasses(step.color, 'text')} text-sm font-medium flex-wrap`}>
                                                {step.tags.map((tag, i) => (
                                                    <React.Fragment key={i}>
                                                        <span>{tag}</span>
                                                        {i < step.tags.length - 1 && <span className="mx-2">•</span>}
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* CTA button */}
                <div className="text-center mt-8 pb-8">
                    <a 
                        href="#contact" 
                        className="inline-block bg-gradient-to-r from-indigo-500 to-blue-500 text-white font-medium py-3 px-8 rounded-lg shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:translate-y-[-2px] transition-all"
                    >
                        Start Your Transformation
                    </a>
                </div>
            </div>
        </section>
    );
}

export default Process;